// Tracciamento visitatori verso CRMiaba (CRM interno IABADUU).
//
// Stesso schema del plugin WordPress `crm-iabaduu-connector`: un cookie
// visitatore di prima parte (`ollylife_cid`) + uno di sessione (`ollylife_sid`)
// sul dominio condiviso `.ollylife.com`, scritti SOLO con il consenso marketing
// (collegare le visite a una persona è profilazione). Ogni visita parte come
// beacon diretto alla Lambda (`POST /public/touchpoint`, origin già in CORS); il
// token del CRM non arriva mai nel browser. I form allegano `getVisitorIds()`
// al body così la Lambda può collegare la conversione (email) alle visite.
//
// ⚠ File gemello: `ollylife-web/src/lib/visitor.ts` — cambia nomi, durate o
// formato in entrambi (e nella Cookie Policy §3).

import { getStoredConsent } from './consent'

const CID_COOKIE = 'ollylife_cid'
const SID_COOKIE = 'ollylife_sid'
const CID_MAX_AGE_S = 60 * 60 * 24 * 365 // 12 mesi, come da Cookie Policy
const SID_MAX_AGE_S = 30 * 60            // sessione: 30 minuti di inattività
const BEACON_URL = `${import.meta.env.VITE_API_BASE_URL as string}/public/touchpoint`

export interface VisitorIds {
  crmClientId?: string
  crmSessionId?: string
}

function domainAttr(): string {
  return window.location.hostname.endsWith('ollylife.com') ? '; domain=.ollylife.com' : ''
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return m && m[1] ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string, maxAge: number): void {
  const secure = window.location.protocol === 'https:' ? '; secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`
    + domainAttr() + secure
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12)
}

function trackingAllowed(): boolean {
  return getStoredConsent()?.categories.marketing === true
}

/** Crea/rinnova i cookie (solo con consenso marketing) e li restituisce. */
function ensureIds(): VisitorIds {
  if (typeof document === 'undefined' || !trackingAllowed()) return {}
  let cid = readCookie(CID_COOKIE)
  if (!cid || !/^[A-Za-z0-9._-]{8,64}$/.test(cid)) cid = uuid()
  writeCookie(CID_COOKIE, cid, CID_MAX_AGE_S) // durata rinnovata a ogni visita
  let sid = readCookie(SID_COOKIE)
  if (!sid || !/^[A-Za-z0-9._-]{8,80}$/.test(sid)) sid = `${cid.slice(0, 8)}_${Date.now().toString(36)}`
  writeCookie(SID_COOKIE, sid, SID_MAX_AGE_S)
  return { crmClientId: cid, crmSessionId: sid }
}

/** Identificativi da allegare al body delle conversioni (vuoto senza consenso). */
export function getVisitorIds(): VisitorIds & { pageUrl?: string } {
  const ids = ensureIds()
  return ids.crmClientId ? { ...ids, pageUrl: window.location.href } : {}
}

/** Consenso marketing revocato: via i cookie del tracciamento. */
export function clearVisitorIds(): void {
  for (const name of [CID_COOKIE, SID_COOKIE]) {
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax` + domainAttr()
  }
}

let lastUrl: string | null = null
let lastAt = 0

/** Beacon di visita della pagina corrente (no-op senza consenso o per i bot). */
export function sendPageView(): void {
  if (typeof window === 'undefined' || navigator.webdriver) return
  const { crmClientId, crmSessionId } = ensureIds()
  if (!crmClientId) return
  const url = window.location.href
  // Doppio effetto (StrictMode, re-render) sulla stessa URL: una visita sola.
  if (url === lastUrl && Date.now() - lastAt < 2000) return
  const referrer = lastUrl ?? document.referrer
  lastUrl = url
  lastAt = Date.now()
  const body = JSON.stringify({ cid: crmClientId, sid: crmSessionId, u: url, r: referrer })
  try {
    if (navigator.sendBeacon?.(BEACON_URL, body)) return
  } catch { /* fallback sotto */ }
  fetch(BEACON_URL, { method: 'POST', body, keepalive: true, headers: { 'content-type': 'text/plain' } })
    .catch(() => { /* best-effort */ })
}
